import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { deburr } from 'lodash';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalTypeaheadMenuPlugin } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import ReactDOM from 'react-dom';
import { TextNode } from 'lexical';

import { getOptionsToDisplay } from '../../../../utils/formulas/formula-reference-utils';
import { FormulaReferenceOption } from '../../../../utils/formulas/types';
import { MentionMenuItem } from './mention-menu-item';
import { $createMentionNode, $replaceTextWithMentionNode } from './mention-node';
import { createMentionTypeaheadOption, MentionTypeaheadOption } from './mention-typeahead-option';
import styles from './formula-editor.scss';

const FORMULA_MENTION_TRIGGER_REGEX =
  /@([\w\u00C0-\u1EF9\u3005-\u3006\u3040-\u30FF\u3130-\u318F\u4E00-\u9FA5\u0600-\u06FF\s-]*)$/;

export const getFormulaMentionTriggerMatch = (text: string) => {
  const match = FORMULA_MENTION_TRIGGER_REGEX.exec(text);

  if (!match) {
    return null;
  }

  return {
    leadOffset: match.index,
    matchingString: match[1],
    replaceableString: match[0]
  };
};

// Carbon makes body-level siblings of an active modal inert. Lexical normally appends its menu
// there, so use Carbon's portal opt-out attribute to keep the typeahead available to pointer input.
export const createMentionsMenuParent = () => {
  if (typeof document === 'undefined') {
    return null;
  }

  const menuParent = document.createElement('div');
  menuParent.className = styles.mentionsMenuPortal;
  menuParent.dataset.notInert = 'true';
  return menuParent;
};

export interface MentionsTypeaheadPluginProps {
  nonSelectableOptionIds?: string[];
  options: FormulaReferenceOption[];
}

/**
 * "@"-trigger autocomplete for formula references, built on Lexical's official
 * `LexicalTypeaheadMenuPlugin`. Equivalent to the `@draft-js-plugins/mention` plugin used by
 * the draft-js formula editor.
 */
export const MentionsTypeaheadPlugin = ({ nonSelectableOptionIds = [], options }: MentionsTypeaheadPluginProps) => {
  const [editor] = useLexicalComposerContext();
  const [menuParent] = useState(createMentionsMenuParent);
  const [queryString, setQueryString] = useState<string | null>(null);

  useEffect(() => {
    if (!menuParent) {
      return;
    }

    document.body.append(menuParent);

    return () => menuParent.remove();
  }, [menuParent]);

  const filteredOptions = useMemo(() => {
    if (queryString === null) {
      return [];
    }
    const query = deburr(queryString.toLocaleLowerCase());
    const matchingOptions = options
      .filter(mention => mention.depth === 0 || deburr(mention.name.toLocaleLowerCase()).indexOf(query) !== -1)
      .filter(mention => !mention.invalid);

    // No cap on the number of suggestions shown here, matching the draft-js editor's behaviour -
    // otherwise a category with many entries (e.g. Profit & Loss/Balance Sheet) could push later
    // categories/sections (e.g. Non financial drivers) out of the list entirely.
    return getOptionsToDisplay(matchingOptions, nonSelectableOptionIds).map(createMentionTypeaheadOption);
  }, [nonSelectableOptionIds, options, queryString]);

  const onSelectOption = useCallback(
    (option: MentionTypeaheadOption, nodeToReplace: TextNode | null, closeMenu: () => void) => {
      if (
        option.mention.disabled ||
        option.mention.depth === 0 ||
        nonSelectableOptionIds.includes(String(option.mention.id))
      ) {
        return;
      }
      editor.update(() => {
        if (nodeToReplace) {
          $replaceTextWithMentionNode(nodeToReplace, String(option.mention.id), option.mention.name);
        } else {
          $createMentionNode(String(option.mention.id), option.mention.name).select();
        }
        closeMenu();
      });
    },
    [editor, nonSelectableOptionIds]
  );

  return (
    <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
      anchorClassName={styles.mentionsMenuAnchor}
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      parent={menuParent ?? undefined}
      triggerFn={getFormulaMentionTriggerMatch}
      options={filteredOptions}
      menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) => {
        if (!anchorElementRef.current || filteredOptions.length === 0) {
          return null;
        }
        return ReactDOM.createPortal(
          <div className={styles.mentionsMenu} role="listbox">
            {filteredOptions.map((option, index) => (
              <MentionMenuItem
                key={option.key}
                index={index}
                option={option}
                isSelected={selectedIndex === index}
                onClick={() => selectOptionAndCleanUp(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
                nonSelectableOptionIds={nonSelectableOptionIds}
              />
            ))}
          </div>,
          anchorElementRef.current
        );
      }}
    />
  );
};
